/** Session state: account, wallet, prefs. Applies the theme and reduced motion to <html>. */
import { applyThemeId, AUTO_THEME, LIGHT_QUERY } from "./themes";
import { applyOccasionLook, loadOccasions, useOccasionsTick } from "./occasions";
import { backSrc } from "./backs";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_PREFS, type Prefs, type Self, type Session } from "@digu/shared";
import { api, ApiError, UNAUTHORIZED_EVENT } from "./api";
import { configureSound } from "./sound";

export type Wallet = Session["wallet"];
/** "unreachable": the first /session answer never came (no signal, API down or 500). Not the same as signed out. */
export type SessionStatus = "loading" | "signed-out" | "signed-in" | "unreachable";
/** Gentle backoff for the automatic retry while unreachable. */
export const RETRY_MS = [3000, 6000, 12000, 20000, 30000];

export interface SessionState {
  status: SessionStatus;
  account: Self | null;
  wallet: Wallet | null;
  prefs: Prefs;
  admin: boolean;
  /** D-196: admins only: Admin › Today's Needs a look (You menu, desktop rail). */
  adminNeeds: number;
  /** D-196: when the newest Shop item went on sale, for the Shop tab's dot. */
  shopNewAt: number | null;
  unread: Session["unread"];
  currentRoom: string | null;
  error: string | null;
  /** Re-fetch /session. */
  refresh: () => Promise<void>;
  /** Install a session returned by /login or /register. */
  signIn: (s: Session) => void;
  signOut: (everywhere?: boolean) => Promise<void>;
  /** Update prefs: saved to the account when signed in (the source of truth), copied on this phone. */
  setPrefs: (patch: Partial<Prefs>) => void;
  /** Patch local account/wallet after a mutation that returns them. */
  setAccount: (a: Self) => void;
  setWallet: (w: Partial<Wallet>) => void;
  systemReducedMotion: boolean;
}

// D-201: a new key. Prefs saved on this phone under the old one move across once, keeping every setting except a theme
// that was only the default of the day (Automatic, Ink, the old Lagoon), which becomes DIGU Classic.
const PREFS_KEY = "digu.prefs.v2";
const OLD_PREFS_KEY = "digu.prefs";
const Ctx = createContext<SessionState | null>(null);

export function readLocalPrefs(): Partial<Prefs> {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return JSON.parse(raw) as Partial<Prefs>;
    const old = localStorage.getItem(OLD_PREFS_KEY);
    if (!old) return {};
    const moved = JSON.parse(old) as Partial<Prefs>;
    if (!moved.theme || moved.theme === "auto" || moved.theme === "ink" || moved.theme === "lagoon") delete moved.theme;
    localStorage.setItem(PREFS_KEY, JSON.stringify(moved));
    localStorage.removeItem(OLD_PREFS_KEY);
    return moved;
  } catch { return {}; }
}
function writeLocalPrefs(p: Prefs): void { try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* private mode */ } }

export function applyTheme(prefs: Prefs, systemReduced: boolean): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  applyThemeId(prefs.theme);
  // D-180: the current occasion's colours over the theme (dark set over dark themes, light over light), unless turned off.
  applyOccasionLook(prefs.occasionLooks !== false);
  root.dataset.reduceMotion = String(prefs.reduceMotion || systemReduced);
  root.dataset.cardFaces = prefs.cardFaces === "large" ? "large" : "classic";
  const meta = document.querySelector('meta[name="theme-color"]');
  const canvas = getComputedStyle(root).getPropertyValue("--color-canvas").trim();
  if (meta && canvas) meta.setAttribute("content", canvas);
  configureSound({ sound: prefs.sound, gameSounds: prefs.gameSounds !== false, reactionSounds: prefs.reactionSounds !== false, timerTick: prefs.timerTick });
}

export function SessionProvider({ children, initial }: { children: ReactNode; initial?: Session | null }): ReactElement {
  const [session, setSession] = useState<Session | null>(initial ?? null);
  const [status, setStatus] = useState<SessionStatus>(initial ? "signed-in" : initial === null ? "signed-out" : "loading");
  const [error, setError] = useState<string | null>(null);
  const [prefs, setPrefsState] = useState<Prefs>(() => mergePrefs(initial?.prefs, readLocalPrefs()));
  const [systemReducedMotion, setSystemReducedMotion] = useState<boolean>(() => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches);
  const navigate = useNavigate();
  const location = useLocation();
  // Prefs belong to the account (audit #13, D-103): the server's copy wins when it arrives, and this phone keeps a copy
  // for the first paint. Two exceptions: a change still on its way to the server, and what was picked before signing in.
  const pendingPrefs = useRef(0);
  const signedOutPicks = useRef<Partial<Prefs>>({});
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setSystemReducedMotion(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  useEffect(() => { applyTheme(prefs, systemReducedMotion); writeLocalPrefs(prefs); }, [prefs, systemReducedMotion]);
  // D-204: the automatic theme follows the device's light or dark mode, live.
  useEffect(() => {
    if (prefs.theme !== AUTO_THEME || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(LIGHT_QUERY);
    const again = () => applyTheme(prefs, systemReducedMotion);
    mq.addEventListener?.("change", again);
    return () => mq.removeEventListener?.("change", again);
  }, [prefs, systemReducedMotion]);
  // The equipped card back reaches the table's piles through one CSS variable.
  const equippedBack = session?.account?.equipped.cardBack ?? null;
  // An occasion back (`back-occ-…`) has its art once its occasion is known.
  const occasionsTick = useOccasionsTick();
  useEffect(() => {
    if (typeof document === "undefined") return;
    // D-204: the free default back follows the theme (--card-back-default: Classic dark or light); any other back is set.
    if (!equippedBack || equippedBack === "cowrie") document.documentElement.style.removeProperty("--card-back-url");
    else document.documentElement.style.setProperty("--card-back-url", `url(${backSrc(equippedBack)})`);
  }, [equippedBack, occasionsTick]);

  const refresh = useCallback(async () => {
    try {
      const s = await api.get<Session>("/session", { silent401: true });
      setSession(s); setStatus("signed-in"); setError(null);
      setPrefsState((local) => (pendingPrefs.current > 0 ? local : mergePrefs(s.prefs, local)));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) { setSession(null); setStatus("signed-out"); setError(null); }
      else { setError(e instanceof Error ? e.message : "Couldn't reach the game."); setStatus((st) => (st === "loading" || st === "unreachable" ? "unreachable" : st)); }
    }
  }, []);

  useEffect(() => { if (initial === undefined) void refresh(); }, [refresh, initial]);
  // Unreachable: try again on our own, with backoff, and at once when the phone says it is back online.
  const [retries, setRetries] = useState(0);
  useEffect(() => {
    if (status !== "unreachable") { setRetries(0); return; }
    const t = setTimeout(() => { void refresh().finally(() => setRetries((n) => n + 1)); }, RETRY_MS[Math.min(retries, RETRY_MS.length - 1)]);
    const onOnline = () => { void refresh(); };
    window.addEventListener("online", onOnline);
    return () => { clearTimeout(t); window.removeEventListener("online", onOnline); };
  }, [status, retries, refresh]);
  // D-180: occasions on start and again on sign-in (`mine` and the owned packs belong to the account).
  const signedIn = status === "signed-in";
  useEffect(() => { void loadOccasions(); }, [signedIn]);

  useEffect(() => {
    const onUnauthorized = () => {
      setSession(null); setStatus("signed-out");
      const p = locationRef.current.pathname;
      if (p !== "/signin" && p !== "/create-account") navigate("/signin", { replace: true, state: { from: p } });
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [navigate]);

  const signIn = useCallback((s: Session) => {
    setSession(s); setStatus("signed-in"); setError(null);
    // Choices made on the sign-in screen's Display and sound sheet go to the account too.
    const picks = signedOutPicks.current; signedOutPicks.current = {};
    const merged = { ...mergePrefs(s.prefs, DEFAULT_PREFS), ...picks };
    setPrefsState(merged);
    if (Object.keys(picks).length) api.post("/profile", { prefs: merged }).catch(() => { /* the phone's copy still applies */ });
  }, []);

  const signOut = useCallback(async (everywhere = false) => {
    try { await api.post(everywhere ? "/logout-all" : "/logout"); } catch { /* the cookie is gone either way */ }
    setSession(null); setStatus("signed-out");
    navigate("/signin", { replace: true });
  }, [navigate]);

  const setPrefs = useCallback((patch: Partial<Prefs>) => {
    if (!session) signedOutPicks.current = { ...signedOutPicks.current, ...patch };
    setPrefsState((p) => {
      const next = { ...p, ...patch };
      if (session) {
        pendingPrefs.current++;
        api.post("/profile", { prefs: next }).catch(() => { /* local copy still applies */ }).finally(() => { pendingPrefs.current--; });
      }
      return next;
    });
  }, [session]);

  const setAccount = useCallback((a: Self) => setSession((s) => (s ? { ...s, account: a } : s)), []);
  const setWallet = useCallback((w: Partial<Wallet>) => setSession((s) => (s ? { ...s, wallet: { ...s.wallet, ...w } } : s)), []);

  const value = useMemo<SessionState>(() => ({
    status, account: session?.account ?? null, wallet: session?.wallet ?? null, prefs, admin: session?.admin ?? false, adminNeeds: session?.adminNeeds ?? 0, shopNewAt: session?.shopNewAt ?? null,
    unread: session?.unread ?? { friends: 0, clubs: 0 }, currentRoom: session?.currentRoom ?? null, error,
    refresh, signIn, signOut, setPrefs, setAccount, setWallet, systemReducedMotion,
  }), [status, session, prefs, error, refresh, signIn, signOut, setPrefs, setAccount, setWallet, systemReducedMotion]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The account's prefs over the defaults; a key the server doesn't have yet keeps this phone's value. */
/** D-201: the move to DIGU Classic happens on the server (migration 044), so the account's own theme simply wins here. */
export function mergePrefs(server: Partial<Prefs> | undefined, local: Partial<Prefs>): Prefs {
  const merged = { ...DEFAULT_PREFS, ...local, ...(server ?? {}) };
  return merged;
}

/** Unread counts for tab-bar dots; zero outside a provider (isolated screen tests). */
/** Re-fetch /session (tab-bar counts and all); a no-op outside the provider. */
export function useSessionRefresh(): () => Promise<void> { return useContext(Ctx)?.refresh ?? noRefresh; }
const noRefresh = async (): Promise<void> => undefined;
/** D-193: true only once the app knows nobody is signed in (false while loading, and outside the provider in tests). */
export function useSignedOut(): boolean { return useContext(Ctx)?.status === "signed-out"; }
export function useUnread(): SessionState["unread"] { return useContext(Ctx)?.unread ?? { friends: 0, clubs: 0 }; }

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession must be used inside SessionProvider");
  return v;
}
